import React, { useState,useEffect,useRef,useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next'
import axios from '../../plugins/axios'
import moment from "moment";
import { mmHgToKPa, celsiusToFahrenheit, percentTommHg, percentToKpa, mmHgToPercent, checkEmptyValue } from '../../plugins/common'
import { useRecoilState } from 'recoil';
import { unitState } from '../../plugins/recoil';
import AlarmType from '../../assets/data/AlarmType.json'

import "./A4.scss";

const alarmTypeMap = AlarmType[0]

// AlarmType field → tableColumn key 매핑 (TabularTrend와 동일)
const ALARM_FIELD_TO_COLUMN_KEY = {
    'hr': 'pulseRate',
    'arrhythmia': 'pulseRate',
    'SpO2': 'spo2Percent',
    'spo2Percent': 'spo2Percent',
    'rrRpm': 'respRate',
    'nibp': 'nibp',
    'ibp1': 'ibp',
    'ibp2': 'ibp',
    'temp1C': 'temperatureCelsius',
    'temp2C': 'temperatureCelsius',
    'awrrRpm': 'respRate',
    'etco2': 'etCo2',
    'etCo2': 'etCo2',
}

// 알람 여부 확인 함수 (priority 0,1,2만 해당)
const hasAlarmMark = (messageTimestamp, columnKey, alarms) => {
    if (!alarms?.length || !columnKey) return false

    const timestamp = moment(messageTimestamp)

    for (const alarm of alarms) {
        const startTime = moment(alarm.startTime)
        const endTime = moment(alarm.endTime)

        if (timestamp.isBefore(startTime) || timestamp.isAfter(endTime)) continue

        const alarmFields = alarmTypeMap[String(alarm.eventCode)]
        if (!alarmFields) continue

        const fields = Array.isArray(alarmFields) ? alarmFields : [alarmFields]
        if (!fields.some(field => ALARM_FIELD_TO_COLUMN_KEY[field] === columnKey)) continue

        const priority = alarm.eventPriority
        if (priority === 0 || priority === 1 || priority === 2) return true
    }

    return false
}

// 첫 페이지는 환자정보 영역이 있어서 row 수가 적음
const FIRST_PAGE_ROWS = 26;
const OTHER_PAGE_ROWS = 30;

// Trend URL Parameter 정리, deviceId, startdate,enddate,selectDate
const V20TrendForm = () => {
    const { t, i18n } = useTranslation()
    const [searchParams] = useSearchParams();
    const isFirstRender = useRef(true);
    const [queryParams, setQueryParams] = useState({})    
    const [deviceInfo, setDeviceInfo] = useState() // 선택한 디바이스 정보  
    const [patientInfo, setPatientInfo] = useState() // 선택한 환자 정보 
    const [detailData, setDetailData] = useState([])
    const [units, setUnits] = useRecoilState(unitState)
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isPageReady, setIsPageReady] = useState(false);
    const [alarmList, setAlarmList] = useState([]);

    // TabularTrendV20과 동일한 컬럼 정의
    const tableColumns = [
        { label: t('leadModal.time'), key: 'messageTimestamp', unitKey: null },
        { label: t('leadModal.patientId'), key: 'devicePatientId', unitKey: null },   
        { label: 'HR/PR', key: 'pulseRate', unitKey: 'HR' },
        { label: 'SpO2', key: 'spo2Percent', unitKey: 'SpO2' },
        { label: 'Temp', key: 'temperatureCelsius', unitKey: 'Temp1' },
        { label: 'RESP', key: 'respRate', unitKey: 'RR' },
        { label: 'NIBP', key: 'nibp', unitKey: 'NIBP' },
        { label: 'IBP', key: 'ibp', unitKey: 'IBP1' },
        { label: 'EtCO2', key: 'etCo2', unitKey: 'EtCO2' },
        { label: 'InCO2', key: 'fiCo2', unitKey: 'InCO2' },
        { label: 'IPI', key: 'ipi', unitKey: 'IPI' },
    ]

    const isSelected = (targetTimeString) => {
        if (!queryParams?.selectTime) return false         
        if (moment.utc(queryParams.selectTime).format('HH:mm:ss') === targetTimeString) return true
        return false
    }

    // 페이지별 데이터 분할
    const pagedData = useMemo(() => {
        if (detailData.length === 0) return [[]]
        
        const pages = []
        let idx = 0
        
        // 첫 페이지
        pages.push(detailData.slice(idx, idx + FIRST_PAGE_ROWS))
        idx += FIRST_PAGE_ROWS
        
        // 나머지 페이지
        while (idx < detailData.length) {
            pages.push(detailData.slice(idx, idx + OTHER_PAGE_ROWS))
            idx += OTHER_PAGE_ROWS
        }
        
        return pages
    }, [detailData])

    const totalPages = pagedData.length

    //Report Info 정보 가져오기
    const getReportInfo = async () => {
         try {
            // 사용자 정보 가져오기
            const res = await axios.get('/user/me')
            if (res.code != '200') throw res

            //언어 세팅
            i18n.changeLanguage(res.data.language);

            const deviceId = queryParams.deviceId // 디폴트 값 설정 

            const resDevice = await axios.get(`/device/${deviceId}`);
            if (resDevice.code != '200') throw resDevice

            setDeviceInfo(resDevice.data);

            const resPatient = await axios.get(`/patient/${resDevice?.data?.patientId}`);
            if (resPatient.code != '200') throw resPatient
            
            setPatientInfo(resPatient.data);
        } catch (patientError) {
            console.warn('환자 정보 가져오기 실패:', patientError);
        }
    }    

    // Unit 재 세팅
    const getUnits = async () => {
        try{
            const unitRes = await axios.get(`/measure/parameter/categories`)
            if (unitRes.code != '200') throw unitRes
            setUnitInfo(unitRes.data.categories)
        }
        catch(err){
            console.warn(err)
        }
    }

    //Unit 세팅
    const setUnitInfo = (categories) => {
        if (!categories) return
        let nibpUnit = categories.find(category => category.name === 'NIBP')
        nibpUnit = nibpUnit.unit || nibpUnit.defaultUnit

        let ibpUnit = categories.find(category => category.name === 'IBP')
        ibpUnit = ibpUnit.unit || ibpUnit.defaultUnit

        let co2Unit = categories.find(category => category.name === 'CO2')
        co2Unit = co2Unit.unit || co2Unit.defaultUnit

        let tempUnit = categories.find(category => category.name === 'TEMP')
        tempUnit = tempUnit.unit || tempUnit.defaultUnit

        let gasUnit = categories.find(category => category.name === 'GAS')
        gasUnit = gasUnit.unit || gasUnit.defaultUnit

        setUnits({
            ...units,
            'NIBP': nibpUnit,
            'IBP': ibpUnit,
            'IBP1': ibpUnit,
            'IBP2': ibpUnit,
            'IBP3': ibpUnit,
            'IBP4': ibpUnit,
            'EtCO2': co2Unit,
            'InCO2': co2Unit,
            'Temp': tempUnit,
            'Temp1': tempUnit,
            'Temp2': tempUnit,
            'GAS': gasUnit
        })
    }

    // TabularTrendV20과 동일한 API 호출
    const getTabularTrendData = async () => {
        try{
            const res = await axios.get(`/trend/hl7/disconnect/vitals?serialNo=${deviceInfo?.serial}&startTime=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(queryParams.endTime).format('YYYY-MM-DDTHH:mm:ss')}Z&device=V20`);

            const processData = res.data.datas            
                .map(item => ({
                    ...item,
                    messageTimestamp: moment(item.messageTimestamp).format('YYYY-MM-DD HH:mm:ss'),
                    nibp: `${checkEmptyValue(item.nibpSys)}/${checkEmptyValue(item.nibpDia)}/${checkEmptyValue(item.nibpMap)}`,
                    ibp: `${checkEmptyValue(item.ibpSys)}/${checkEmptyValue(item.ibpDia)}/${checkEmptyValue(item.ibpMean)}`,
                }));

            setDetailData(processData)
        }
        catch(err){
            console.warn('getTabularTrendData error:', err);
            setIsDataLoaded(true)
        }
    }

    // Alarm List 가져오기
    const getAlarmList = async () => {
        try {
            const res = await axios.get(`/trend/alarm/event?serialNo=${deviceInfo?.serial}&startTime=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(queryParams.endTime).format('YYYY-MM-DDTHH:mm:ss')}Z`)
            if (res.code != '200') throw res
            const alarmData = res.data.events.map((alarm, idx) => ({
                ...alarm,
                alarmNum: idx,
                startTime: moment(alarm.startTime).format('YYYY-MM-DD HH:mm:ss'),
                endTime: moment(alarm.endTime).format('YYYY-MM-DD HH:mm:ss'),
            }))
            setAlarmList(alarmData)
        } catch (err) {
            console.warn('getAlarmList error:', err)
            setAlarmList([])
        } finally {
            setIsDataLoaded(true)
        }
    }

    // 단위 변환 함수
    const convertUnit = (parameter,value) => {
        const nibpSeries = ['NIBP']
        const ibpSeries = ['IBP1', 'IBP2', 'IBP3', 'IBP4']
        const co2Series = ['EtCO2', 'InCO2']
        const tempSeries = ['Temp1', 'Temp2']        
        const gasSeries = ['gasCo2', 'gasDes', 'gasN2o', 'gasO2', 'gasSev']
        const otherSeries = ['HR', 'RR', 'awRR', 'SpO2', 'arrhythmia', 'IPI']

        const convertFunctions = {
            mmHgToKPa,
            celsiusToFahrenheit,
            percentTommHg,
            percentToKpa,
            mmHgToPercent
        };

        let unit = undefined;
        let functionName = undefined;                

        if (nibpSeries.includes(parameter)) {
            unit = units["NIBP"];
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa';
            }
        } else if (ibpSeries.includes(parameter)) {
            const prefix = parameter.match(/^IBP[1-4]/)?.[0];
            unit = units[prefix];
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa';
            }
        } else if (co2Series.includes(parameter)) {
            unit = units[parameter];            
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa';
            }
            else if(unit === '%'){
                functionName = 'mmHgToPercent';
            }
        } else if (tempSeries.includes(parameter)) {
            unit = units[parameter];
            if(unit === '°F') {
                functionName = 'celsiusToFahrenheit';
            }
        } 
        else if (gasSeries.includes(parameter)) {            
            unit = units["GAS"];            
            if(unit === 'mmHg') {
                functionName = 'percentTommHg';
            }
            else if(unit === 'kPa'){
                functionName = 'percentToKpa';
            }
        } else if(otherSeries.includes(parameter)) {
            unit = units[parameter];
        }

        let label = value;
        if (functionName && typeof convertFunctions[functionName] === 'function') {
            if(nibpSeries.includes(parameter) || ibpSeries.includes(parameter) || gasSeries.includes(parameter)){
                const splitValue = value.split('/');
                let newLabel = '';
                splitValue.map((value,index) => {
                    const convertedValue = value === '-' ? '-' : String(convertFunctions[functionName](value));
                    newLabel += convertedValue + `${index == splitValue.length - 1 ? '' : '/'}`;     
                })
                label = newLabel;
            }else {
                if(value == '-'){
                    return value;
                }else{
                    label = convertFunctions[functionName](value);
                }
            }
        }
        return label;
    }

    // 셀 값 포맷팅 함수 (TabularTrendV20과 동일)
    const getFormatValue = (col, item) => {
        const rawValue = item[col.key]
        
        // Unit 변환이 필요 없는 컬럼
        if (!col.unitKey) return checkEmptyValue(rawValue)
        
        // Temperature - 소수점 한자리 포맷
        if (col.key === 'temperatureCelsius') {
            if (rawValue == null) return '-'
            const formatted = (Math.floor(rawValue * 10) / 10).toFixed(1)
            return convertUnit(col.unitKey, formatted)
        }
        
        // Unit 변환
        const value = checkEmptyValue(rawValue)
        return convertUnit(col.unitKey, String(value))
    }

    // 프린터 함수
    const handlePrint = () => {
        console.log('프린트 시작');
        
        // 클래스 제거 후 다시 추가 (리프레시 효과)
        document.documentElement.classList.remove("waveform-active");
        document.body.classList.remove("waveform-active");
        
        setTimeout(() => {
            document.documentElement.classList.add("waveform-active");
            document.body.classList.add("waveform-active");
            
            setTimeout(() => {
                console.log('페이지 수:', document.querySelectorAll('.a4-page').length);
                window.print();
            }, 500);
        }, 100);
    };

    // 테이블 헤더 렌더링
    const renderTableHeader = () => (
        <tr className={`${tableLine}`}>
            {tableColumns.map((col, idx) => (
                <th className={`${tableHeaderFont} text-left`} key={`tabular-header-${idx}`}>
                    <strong>{col.label}</strong>
                    {col.unitKey && units[col.unitKey] && (
                        <p className="text-[#848484] font-pretendard text-[24px] font-medium leading-[140%]">
                            ({units[col.unitKey]})
                        </p>
                    )}
                </th>
            ))}
        </tr>
    )

    // 셀 값이 비어있는지 확인하는 함수 (빈 값이면 ＊ 표기 안함)
    const isEmptyValue = (col, item) => {
        const rawValue = item[col.key]
        if (rawValue == null || rawValue === '') return true
        // '-' 또는 '-/-/-' 같은 빈 표기 체크
        const strValue = String(rawValue)
        if (strValue === '-' || /^[-/]+$/.test(strValue)) return true
        return false
    }

    // 테이블 바디 렌더링
    const renderTableBody = (pageData) => (
        pageData.map((item, index) => (
            <tr key={index} className={`${tableLine}`}>
                {tableColumns.map((col, colIdx) => {
                    if (colIdx === 0) {
                        return (
                            <td key={`tabular-data-${index}-${colIdx}`} className={`${tableBodyFont} ${isSelected(moment(item.messageTimestamp).format('HH:mm:ss')) ? 'border-[4px] border-dotted border-[#000]' : ''}`}>
                                {moment(item.messageTimestamp).format('HH:mm:ss')}
                            </td>
                        )
                    }
                    const value = getFormatValue(col, item)
                    const showAlarmMark = col.unitKey && !isEmptyValue(col, item) && hasAlarmMark(item.messageTimestamp, col.key, alarmList)
                    return (
                        <td key={`tabular-data-${index}-${colIdx}`} className={`${tableBodyFont}`}>
                            {value}{showAlarmMark ? '＊' : ''}
                        </td>
                    )
                })}
            </tr>
        ))
    )

    useEffect(() => {
        setQueryParams({
            deviceId: searchParams.get('deviceid'),
            startTime: searchParams.get('starttime'),
            endTime: searchParams.get('endtime'),
            selectTime: searchParams.get('selecttime'),                        
        });        

        //Page 스타일링
        const style = document.createElement("style");
            style.innerHTML = `
            @page {
                size: 3508px 2480px;
                margin: 0;
            }
            `;
        document.head.appendChild(style);

        // 스타일 클래스 추가
        document.documentElement.classList.add("waveform-active");
        document.body.classList.add("waveform-active");        

        return () => {
            document.documentElement.classList.remove("waveform-active");
            document.body.classList.remove("waveform-active");
            document.head.removeChild(style); // 라우트 빠져나갈 때 제거
        };
    }, []);

    useEffect(() => {
        if(isFirstRender.current){
            isFirstRender.current = false;
            return;
        }

        console.log('queryParams : ', queryParams);
        getReportInfo(); //Device & Patient정보 가져오기.
    }, [queryParams])    

    useEffect(() => {
        if(deviceInfo){
            getUnits();
        }
    }, [deviceInfo])

    useEffect(() => {
        if(units && deviceInfo){
            getTabularTrendData();
        }
    }, [units])

    // detailData가 세팅되면 AlarmList 가져오기
    useEffect(() => {
        if(detailData?.length > 0 && deviceInfo){
            getAlarmList();
        }
    }, [detailData])

    useEffect(() => {
        if (isDataLoaded) {
            // DOM 렌더링 및 Canvas 그리기 완료를 위한 충분한 대기 시간
            const renderTimer = setTimeout(() => {
                setIsPageReady(true);
            }, 1500); // Canvas 렌더링을 고려한 여유 시간

            return () => clearTimeout(renderTimer);
        }
    }, [isDataLoaded]);

    // 페이지 준비 완료 시 자동 프린트
    useEffect(() => {
        if (isPageReady) {
            // URL 파라미터로 자동 프린트 제어
            const urlParams = new URLSearchParams(window.location.search);
            const autoPrint = urlParams.get('autoPrint');
            const delay = parseInt(urlParams.get('delay')) || 3000;
            
            if (autoPrint === 'true' || autoPrint === null) { // 기본값은 자동 프린트
                setTimeout(() => {
                    handlePrint();
                }, delay);
            }
        }
    }, [isPageReady]);

    //인쇄 하거나 취소하고 창 꺼지기
    window.onafterprint = () => {
        setTimeout( () => {
            window.close();
        },1000)
    };

    // 스타일 정의
    const patientInfoFontBold = 'text-black font-medium text-[48px] leading-[110%] font-pretendard';
    const patientInfoFont = 'text-black font-thin text-[36px] leading-[40px] font-pretendard';
    const titleFontBold = 'text-black font-medium text-[48px] leading-[110%] font-pretendard';
    const subTitleFont = 'text-black font-thin text-[36px] leading-[40px] font-pretendard';
    const tableHeaderFont = 'text-black text-center font-pretendard text-[28px] font-medium leading-[110%] bg-[#fff] py-[24px]';
    const tableBodyFont = 'text-black text-center font-pretendard text-[32px] font-light leading-[110%] py-[15px] whitespace-nowrap';
    const tableLine = 'border-b-[1px] border-opacity-50 border-[#5E5E5E]';

    return (
        <div className="document-container bg-gray-100 p-20 font-sans min-h-screen">
            {/* 첫 번째 페이지: 환자 정보 + Tabular Trend */}
            <div className="a4-page-landscape mx-auto bg-white shadow-lg relative box-border">
                <div className="w-full h-full flex flex-col px-[146px] pt-[140px] pb-[66px]">                    
                    <div>
                        <div className="w-full flex flex-col pb-[24px] border-b-[4px] border-[#5E5E5E] ">
                            <div className='w-full flex items-center justify-start gap-[122px]'>
                                <div className="flex">
                                    <strong className={patientInfoFontBold}>{t("print.common.patientId")} : </strong>
                                    <strong className={patientInfoFontBold}>&nbsp;{patientInfo?.devicePatientId || '---'}</strong>
                                </div>
                                <div className="flex">
                                    <strong className={patientInfoFontBold}>{t("print.common.name")} : </strong>
                                    <strong className={patientInfoFontBold}>&nbsp;{patientInfo?.name || '---'}</strong>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex items-center justify-between py-[32px] border-b-[4px] border-[#5E5E5E]/50 mb-[80px]'>
                            <div className="flex">
                                <span className={patientInfoFont}>{t("print.common.admitted")} : </span>
                                <span className={patientInfoFont}>&nbsp;{patientInfo?.createdAt ? moment(patientInfo?.createdAt).format('YYYY-MM-DD HH:mm') : '---'}</span>
                            </div>
                            <div className="flex">
                                <span className={patientInfoFont}>{t("print.common.category")} : </span>
                                <span className={patientInfoFont}>&nbsp;{patientInfo?.category || '---'}</span>
                            </div>
                            <div className="flex">
                                <span className={patientInfoFont}>{t("print.common.dateOfBirth")} : </span>
                                <span className={patientInfoFont}>&nbsp;{patientInfo?.birthDate || '---'}</span>
                            </div>
                            <div className="flex">
                                <span className={patientInfoFont}>{t("print.common.gender")} : </span>
                                <span className={patientInfoFont}>&nbsp;{patientInfo?.gender === 'M' ? t('print.common.male') : patientInfo?.gender === 'F' ? t('print.common.female') : t('print.common.notSelected')}</span>
                            </div>
                            <div className="flex">
                                <span className={patientInfoFont}>{t("print.common.height")}/{t("print.common.weight")} : </span>
                                <span className={patientInfoFont}>&nbsp;{patientInfo?.height || '0'}cm / {patientInfo?.weight || '0'}kg</span>
                            </div>
                        </div>                        
                    </div>
                    {/* Content - Tabular Trend Header */}
                    <div className="w-full flex items-center justify-start pb-[24px] border-b-[4px] border-[#5E5E5E]">
                        <div className="flex items-center justify-center gap-[20px]">
                            <strong className={titleFontBold}>
                                {t("print.trendForm.tabularTrend")}
                            </strong>
                            <strong className={subTitleFont}>
                                ( *{t("print.trendForm.alarmSituation")} )
                            </strong>
                        </div>
                    </div>

                    {/* Content - Tabular Trend Table (첫 페이지) */}
                    <div className="w-full relative flex-1">
                        <div className="w-full flex flex-col">                                            
                            <div className="w-full flex items-center justify-between flex-wrap">
                                <div title="Table" className="w-full flex flex-col justify-center items-center">
                                    <table className="w-full">
                                        <thead>
                                            {renderTableHeader()}
                                        </thead>
                                        <tbody>
                                            {pagedData[0] && pagedData[0].length > 0 
                                                ? renderTableBody(pagedData[0])
                                                : (
                                                    <tr>
                                                        <td colSpan={tableColumns.length}>No Data Available</td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="w-full flex items-center justify-end">
                        <div>
                            <span className="text-black text-center font-pretendard text-[24px] font-medium leading-[110%]">
                                (1/{totalPages})
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2페이지 이후: 추가 데이터 페이지 */}
            {pagedData.slice(1).map((pageData, pageIdx) => (
                <div key={`page-${pageIdx + 2}`} className="a4-page-landscape mx-auto bg-white shadow-lg relative box-border">
                    <div className="w-full h-full flex flex-col px-[146px] pt-[140px] pb-[66px]">
                        {/* Header */}
                        <div className="w-full flex items-center justify-start pb-[24px] border-b-[4px] border-[#5E5E5E]">
                            <div className="flex items-center justify-center gap-[20px]">
                                <strong className={titleFontBold}>
                                    {t("print.trendForm.tabularTrend")}
                                </strong>
                                <strong className={subTitleFont}>
                                    ( *{t("print.trendForm.alarmSituation")} )
                                </strong>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="w-full relative flex-1">
                            <div className="w-full flex flex-col">                                            
                                <div className="w-full flex items-center justify-between flex-wrap">
                                    <div title="Table" className="w-full flex flex-col justify-center items-center">
                                        <table className="w-full">
                                            <thead>
                                                {renderTableHeader()}
                                            </thead>
                                            <tbody>
                                                {renderTableBody(pageData)}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="w-full flex items-center justify-end">
                            <div>
                                <span className="text-black text-center font-pretendard text-[24px] font-medium leading-[110%]">
                                    ({pageIdx + 2}/{totalPages})
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

export default V20TrendForm


